import { unzipSync } from 'fflate';

// Reads the first sheet of an .xlsx into rows of strings, in the browser.
// An .xlsx is a zip of XML parts: the workbook names the sheets, the rels
// file points each sheet at a part, shared strings hold the repeated text,
// and the sheet holds cells addressed by "B7"-style refs. No other library
// is needed for that, and the file never leaves the device.

export const XLSX_ERROR = "This file isn't a readable .xlsx. Try saving it as CSV.";

// Zip bombs: no part we read is ever larger than this once unpacked.
const MAX_PART_BYTES = 40 * 1024 * 1024;

export type XmlParser = (xml: string) => Document;

function defaultParser(xml: string): Document {
  const Parser = (globalThis as { DOMParser?: typeof DOMParser }).DOMParser;
  if (!Parser) throw new Error(XLSX_ERROR);
  const doc = new Parser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) throw new Error(XLSX_ERROR);
  return doc;
}

function decode(bytes: Uint8Array): string {
  return new TextDecoder('utf-8').decode(bytes);
}

/** "A" -> 0, "Z" -> 25, "AA" -> 26. */
export function columnIndex(letters: string): number {
  let n = 0;
  for (const ch of letters.toUpperCase()) {
    const code = ch.charCodeAt(0);
    if (code < 65 || code > 90) return -1;
    n = n * 26 + (code - 64);
  }
  return n - 1;
}

function byName(parent: Document | Element, name: string): Element[] {
  return Array.from(parent.getElementsByTagNameNS('*', name));
}

/** Text of an element's <t> children, rich-text runs included, skipping
 *  phonetic hints (<rPh>) that Excel adds for East Asian text. */
function textOf(el: Element): string {
  let out = '';
  for (const child of Array.from(el.children)) {
    const tag = child.localName;
    if (tag === 't') out += child.textContent ?? '';
    else if (tag === 'r') out += textOf(child);
  }
  return out;
}

function resolvePart(target: string): string {
  const clean = target.replace(/^\/+/, '');
  return clean.startsWith('xl/') ? clean : `xl/${clean}`;
}

function firstSheetPath(files: Record<string, Uint8Array>, parse: XmlParser): string {
  const book = files['xl/workbook.xml'];
  if (!book) throw new Error(XLSX_ERROR);
  const sheet = byName(parse(decode(book)), 'sheet')[0];
  if (!sheet) throw new Error(XLSX_ERROR);
  const rid =
    sheet.getAttribute('r:id') ??
    sheet.getAttributeNS(
      'http://schemas.openxmlformats.org/officeDocument/2006/relationships',
      'id'
    );
  const rels = files['xl/_rels/workbook.xml.rels'];
  if (rid && rels) {
    for (const rel of byName(parse(decode(rels)), 'Relationship')) {
      if (rel.getAttribute('Id') === rid) {
        const target = rel.getAttribute('Target');
        if (target) return resolvePart(target);
      }
    }
  }
  return 'xl/worksheets/sheet1.xml';
}

function cellNumber(raw: string): string {
  if (raw.trim() === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  // A phone stored as a number must come out as digits, not 9.09555E+9 or
  // "9095550142.0". Integers print in full; anything else keeps its text.
  if (Number.isInteger(n) && Math.abs(n) < 1e21) return BigInt(n).toString();
  return raw;
}

export function readXlsxRows(bytes: Uint8Array, parse: XmlParser = defaultParser): string[][] {
  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(bytes, {
      filter: (f) =>
        f.originalSize <= MAX_PART_BYTES &&
        (f.name === 'xl/workbook.xml' ||
          f.name === 'xl/_rels/workbook.xml.rels' ||
          f.name === 'xl/sharedStrings.xml' ||
          /^xl\/worksheets\/[^/]+\.xml$/.test(f.name)),
    });
  } catch {
    throw new Error(XLSX_ERROR);
  }

  const sheetBytes = files[firstSheetPath(files, parse)];
  if (!sheetBytes) throw new Error(XLSX_ERROR);

  const shared: string[] = [];
  const sharedBytes = files['xl/sharedStrings.xml'];
  if (sharedBytes) {
    for (const si of byName(parse(decode(sharedBytes)), 'si')) shared.push(textOf(si));
  }

  const rows: string[][] = [];
  let nextRow = 0;
  for (const rowEl of byName(parse(decode(sheetBytes)), 'row')) {
    const declared = Number(rowEl.getAttribute('r'));
    const rowIndex = Number.isInteger(declared) && declared > 0 ? declared - 1 : nextRow;
    nextRow = rowIndex + 1;
    const cells: string[] = [];
    let nextCol = 0;
    for (const c of Array.from(rowEl.children)) {
      if (c.localName !== 'c') continue;
      const ref = /^([A-Za-z]+)\d*$/.exec(c.getAttribute('r') ?? '');
      let col = ref ? columnIndex(ref[1]) : nextCol;
      if (col < 0) col = nextCol;
      nextCol = col + 1;
      const type = c.getAttribute('t') ?? 'n';
      const v = Array.from(c.children).find((e) => e.localName === 'v')?.textContent ?? '';
      let value = '';
      if (type === 's') value = shared[Number(v)] ?? '';
      else if (type === 'inlineStr') {
        const is = Array.from(c.children).find((e) => e.localName === 'is');
        value = is ? textOf(is) : '';
      } else if (type === 'str') value = v;
      else if (type === 'b') value = v === '1' ? 'TRUE' : 'FALSE';
      else if (type === 'e') value = '';
      else value = cellNumber(v);
      while (cells.length < col) cells.push('');
      cells[col] = value;
    }
    while (cells.length > 0 && cells[cells.length - 1].trim() === '') cells.pop();
    while (rows.length < rowIndex) rows.push([]);
    rows[rowIndex] = cells;
  }
  // Drop the formatted-but-empty rows Excel leaves below the data.
  while (rows.length > 0 && rows[rows.length - 1].length === 0) rows.pop();
  return rows;
}
